import { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { IPropsWithChildrenAndClassName } from 'types/common.interface';

import NftCardSold from 'components/nftCard/_new/nftCardSold/NftCardSold';
import VenueBackground from 'components/venueBackground/VenueBackground';

import { useClamped } from 'hooks/useClamped';

import { parseThousandWithEmpty } from 'utils/parser.utils';

import { ReactComponent as ImageBgSvg } from 'assets/nft-card/nft-card-image-background.svg';

import { useNftCardSize } from '../hooks';
import { useNftCardContext } from '../nftCardContext/NftCardContext';
import NftCardMarker from '../nftCardMarker/NftCardMarker';
import NftCardOverbid from '../nftCardOverbid/NftCardOverbid';
import NftCardPanel from '../nftCardPanel/NftCardPanel';
import NftCardShares from '../nftCardShares/NftCardShares';
import NftCardToggleButton from '../nftCardToggleButton/NftCardToggleButton';
import NftCategoryIdBadge from '../nftCategoryIdBadge/NftCategoryIdBadge';

const renderShares = (shares: string | number | undefined) => {
  if (typeof shares === 'number') return parseThousandWithEmpty(shares);

  return shares;
};

export interface INftCardFrontProps extends IPropsWithChildrenAndClassName {
  toggleable?: boolean;
  asLinkTo?: string;
  isOverbid?: boolean;
  isSold?: boolean;
  nameClassName?: string;
  nameClampedClassName?: string;
  cardMarkerClassName?: string;
  cardMarkerSizeClassName?: string;
  cardMarkerImageClassName?: string;
  addressClassName?: string;
  sharesClassName?: string;
  categoryIdBadgeClassName?: string;
  nameContainerClassName?: string;
  nftCardPanelClassName?: string;
  imageTopRightContent?: ReactNode;
  imageBottomContent?: ReactNode;
  /** Hide the big centred category marker and show it inline in the top-left badge instead. */
  markerInBadge?: boolean;
}

const NftCardFront: FC<INftCardFrontProps> = ({
  children,
  toggleable = true,
  asLinkTo,
  isOverbid,
  isSold,
  nameClassName,
  nameClampedClassName,
  cardMarkerClassName,
  cardMarkerSizeClassName,
  cardMarkerImageClassName,
  addressClassName,
  sharesClassName,
  categoryIdBadgeClassName,
  nameContainerClassName,
  nftCardPanelClassName,
  imageTopRightContent,
  imageBottomContent,
  markerInBadge = false,
}) => {
  const { asset } = useNftCardContext();
  const { isCardLarge, isCardMedium, isCardSmall } = useNftCardSize();
  const isNotSmallCard = isCardLarge || isCardMedium;
  const { ref, clamped } = useClamped();

  const cardFront = (
    <>
      {isSold && <NftCardSold />}
      <div className="tw-relative tw-overflow-hidden tw-rounded-2xl tw-bg-backgroundColorLight">
        {isOverbid && <NftCardOverbid />}
        <NftCategoryIdBadge className={categoryIdBadgeClassName} showMarker={markerInBadge} />
        {imageTopRightContent && (
          // right-11 keeps the badge clear of the flip button (top-right, ~39px wide)
          <div className="tw-absolute tw-right-11 tw-top-2.5 tw-z-10">{imageTopRightContent}</div>
        )}
        {toggleable && <NftCardToggleButton />}
        <VenueBackground
          backgroundAnimationUrl={asset?.animationUrl}
          backgroundImageUrl={asset?.imageUrl}
          className="tw-w-full tw-pb-[100%]"
          autoPlay={isNotSmallCard}
          lazy
        />
        <div
          className={twMerge(
            'tw-relative tw-grid',
            // before element added to prevent space when card is scaled
            'before:tw-absolute before:-tw-top-px before:tw-left-0.5 before:tw-right-0.5 before:tw-h-0.5 before:tw-w-[calc(100%_-_4px)] before:tw-bg-backgroundColorLight before:tw-content-[""]',
            isCardLarge && 'tw-gap-3 tw-p-5 tw-pt-10',
            isCardMedium && 'tw-gap-1.5 tw-p-3 tw-pt-7.5',
            // markerInBadge hides the big centred marker, so reclaim the top space it left.
            isCardSmall && (markerInBadge ? 'tw-gap-1.5 tw-p-3 tw-pt-3' : 'tw-gap-1.5 tw-p-3 tw-pt-5')
          )}
        >
          {/* The curved notch exists to cradle the centred marker — drop it when
              the marker moves into the badge, otherwise it's a stray blue bump. */}
          {!markerInBadge && <ImageBgSvg className="tw-absolute tw-inset-x-0 -tw-top-4.5 tw-w-full" />}
          {!markerInBadge && (
            <NftCardMarker
              className={cardMarkerClassName}
              markerClassName={cardMarkerSizeClassName}
              imageClassName={cardMarkerImageClassName}
            />
          )}
          {imageBottomContent ? (
            <div className="tw-mx-auto tw-w-fit">{imageBottomContent}</div>
          ) : (
            // markerInBadge cards surface shares in their data panel ("Shares
            // listed"), so the chip here would be a duplicate — hide it.
            !markerInBadge &&
            asset?.shares && (
              <div className="tw-mx-auto tw-w-fit tw-rounded-2xl tw-bg-backgroundColorDark tw-px-2.5 tw-py-1.5">
                <NftCardShares size="sm" className={sharesClassName}>
                  {renderShares(asset.shares)}
                </NftCardShares>
              </div>
            )
          )}
          <NftCardPanel
            className={twMerge(
              isNotSmallCard && 'tw-h-[68px] tw-p-2.5',
              isCardSmall && 'tw-h-[54px] tw-px-2.5 tw-py-2',
              nftCardPanelClassName
            )}
          >
            <div
              ref={ref}
              className={twMerge(
                'tw-mb-1 tw-flex tw-items-center tw-justify-center',
                isNotSmallCard && 'tw-h-7.5',
                isCardSmall && 'tw-h-5',
                nameContainerClassName
              )}
            >
              <p
                className={twMerge(
                  'tw-line-clamp-2 tw-px-0.5 tw-font-bold tw-uppercase tw-text-white',
                  isNotSmallCard && 'tw-text-[26px]/[20px]',
                  isCardSmall && clamped ? 'tw-text-[18px]/[14px]' : 'tw-text-[24px]/[19px]',
                  clamped ? nameClampedClassName : nameClassName
                )}
              >
                {asset?.name}
              </p>
            </div>
            <p
              className={twMerge(
                'tw-font-semibold tw-text-menuLinkColor',
                isNotSmallCard && 'tw-text-[13px]/[12px]',
                isCardSmall && 'tw-text-[12px]/[12px]',
                addressClassName
              )}
            >
              {asset?.address}
            </p>
          </NftCardPanel>
          {isNotSmallCard && children}
        </div>
      </div>
      {isCardSmall && children}
    </>
  );

  return asLinkTo ? (
    <Link className="backface-hidden tw-relative tw-z-0 tw-block" to={asLinkTo}>
      {cardFront}
    </Link>
  ) : (
    <div className="backface-hidden tw-relative tw-z-0">{cardFront}</div>
  );
};

export default NftCardFront;
